import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function AIIntegrationPage() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-white pt-32 pb-20">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full mb-6">
                AI SOLUTIONS
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                AI Integration
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Solutions
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Seamlessly integrate AI capabilities into your existing systems. Enhance your current infrastructure with intelligent features without disruption.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Integrate AI Now
                </a>
                <a href="/case-studies" className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:border-purple-500 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Integration Examples
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Types */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                INTEGRATION OPTIONS
              </div>
              <h2 className="text-4xl font-bold mb-4">What We Integrate</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Add AI capabilities to any part of your technology stack
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-apps-line',
                  title: 'CRM & ERP Systems',
                  description: 'Enhance Salesforce, SAP, Oracle, and other enterprise systems with AI-powered insights and automation.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: 'ri-shopping-cart-line',
                  title: 'E-commerce Platforms',
                  description: 'Add intelligent product recommendations, dynamic pricing, and personalized experiences to your store.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: 'ri-customer-service-line',
                  title: 'Customer Support',
                  description: 'Integrate AI chatbots and virtual assistants into your support channels for 24/7 assistance.',
                  gradient: 'from-indigo-500 to-blue-500'
                },
                {
                  icon: 'ri-mail-line',
                  title: 'Marketing Tools',
                  description: 'Enhance email marketing, social media, and advertising platforms with AI-driven optimization.',
                  gradient: 'from-violet-500 to-purple-500'
                },
                {
                  icon: 'ri-file-text-line',
                  title: 'Document Management',
                  description: 'Add intelligent document processing, classification, and extraction to your workflows.',
                  gradient: 'from-blue-500 to-indigo-500'
                },
                {
                  icon: 'ri-bar-chart-box-line',
                  title: 'Analytics Platforms',
                  description: 'Augment BI tools with predictive analytics and automated insight generation.',
                  gradient: 'from-purple-500 to-violet-500'
                }
              ].map((integration, index) => (
                <div 
                  key={index}
                  className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${integration.gradient} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${integration.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{integration.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{integration.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration Approach */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                OUR APPROACH
              </div>
              <h2 className="text-4xl font-bold mb-4">Seamless Integration Process</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A methodical approach ensuring minimal disruption to your operations
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Assessment',
                  description: 'Analyze your current systems and identify integration points',
                  icon: 'ri-search-line'
                },
                {
                  step: '02',
                  title: 'Planning',
                  description: 'Design integration architecture and migration strategy',
                  icon: 'ri-draft-line'
                },
                {
                  step: '03',
                  title: 'Implementation',
                  description: 'Deploy AI capabilities with thorough testing',
                  icon: 'ri-tools-line'
                },
                {
                  step: '04',
                  title: 'Optimization',
                  description: 'Monitor performance and continuously improve',
                  icon: 'ri-line-chart-line'
                }
              ].map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
                    <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                      {step.step}
                    </div>
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-4">
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                BENEFITS
              </div>
              <h2 className="text-4xl font-bold mb-4">Why Integrate AI?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Transform your existing systems with intelligent capabilities
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: 'ri-funds-line',
                  title: 'Cost Effective',
                  description: 'Leverage existing infrastructure instead of rebuilding from scratch'
                },
                {
                  icon: 'ri-time-line',
                  title: 'Quick Deployment',
                  description: 'Faster time to value with minimal disruption'
                },
                {
                  icon: 'ri-arrow-up-line',
                  title: 'Enhanced Performance',
                  description: 'Boost efficiency and accuracy of current systems'
                },
                {
                  icon: 'ri-scales-line',
                  title: 'Scalable Solution',
                  description: 'Grow AI capabilities as your needs evolve'
                }
              ].map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 text-center hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-full mx-auto mb-6">
                    <i className={`${benefit.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Integrations */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                POPULAR INTEGRATIONS
              </div>
              <h2 className="text-4xl font-bold mb-4">Platforms We Work With</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Seamless integration with leading enterprise platforms
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {[
                'Salesforce',
                'Microsoft 365',
                'SAP',
                'Oracle',
                'Shopify',
                'WordPress',
                'HubSpot',
                'Zendesk',
                'Slack',
                'Jira',
                'ServiceNow',
                'Workday'
              ].map((platform, index) => (
                <div 
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-base font-semibold text-gray-800">{platform}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Enhance Your Systems With
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                AI Integration
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Let's explore how AI can supercharge your existing technology infrastructure.
            </p>
            <a 
              href="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Discuss Integration Options
            </a>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}